import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Tokens } from '../models/auth.model';
import {
  CompanyScopeClaim,
  CurrentUserCompanies,
} from '../models/company-scope.model';
import { AuthService } from './auth.service';
import { MyPermissionsService } from './my-permissions.service';

/**
 * ECDEV-256: the companies the signed-in user can act in, and switching between them.
 *
 * Switching re-issues the JWT server-side and hard-reloads the app. A reload rather than an
 * in-place refresh because there is no state-invalidation infrastructure here — no store, no
 * resolvers, no RouteReuseStrategy — and every page already re-fetches on mount, which is exactly
 * what `autoSignIn()` does at bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class CompanyScopeService {
  /**
   * Grid state that is shaped by the active company: the column set comes from that company's
   * dynamic fields, so a layout saved under one company must not be applied under another.
   */
  static readonly companyScopedStorageKeys = [
    'table',
    'reports_table',
    'tc_saved_orders',
    'tc_saved_reports',
    'multiFO',
    'multiFR',
  ];

  companies = new BehaviorSubject<CurrentUserCompanies | null>(null);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private myPermissions: MyPermissionsService
  ) {}

  getCompanies(): Observable<CurrentUserCompanies> {
    return this.http
      .get<CurrentUserCompanies>(`/personal/companies`)
      .pipe(tap((resp) => this.companies.next(resp)));
  }

  /**
   * Swaps the session to another company. The server rejects a company the user does not belong
   * to, so this is a request, not an assertion.
   */
  switchCompany(companyId: string): Observable<Tokens> {
    return this.applySwitch({ companyId, allCompanies: false });
  }

  switchToAllCompanies(): Observable<Tokens> {
    return this.applySwitch({ companyId: null, allCompanies: true });
  }

  /**
   * The key the grid's saved layout and filters hang off. Derived from the token so it survives a
   * reload, and includes the scope because All mode shows an extra Company column.
   */
  currentScopeKey(): string {
    const tokenData = this.authService.getTokenData();
    if (!tokenData) {
      return 'none';
    }

    try {
      const claims = new JwtHelperService().decodeToken(
        (JSON.parse(tokenData) as Tokens).token,
      );

      // The claim carries the enum *name* ("Single"/"All"), unlike the API's numeric form.
      const scope = claims?.['company.scope'] ?? CompanyScopeClaim.Single;
      const companyId = claims?.['company.id'];

      // CM staff have no company and have always seen everything, so they get one stable bucket.
      if (!companyId) {
        return 'cm';
      }

      return scope === CompanyScopeClaim.All ? 'all' : companyId;
    } catch {
      return 'none';
    }
  }

  private applySwitch(body: {
    companyId: string | null;
    allCompanies: boolean;
  }): Observable<Tokens> {
    // Not /tokens/switch-company: TokenInterceptor attaches no bearer to /tokens* URLs, so an
    // authenticated action on that path always 401s — and the error interceptor turns that into a
    // logout, so the switch would have signed the user out.
    return this.http.post<Tokens>(`/personal/switch-company`, body).pipe(
      tap((tokens) => {
        // Drop grid state before the new token lands, so the reload cannot briefly reconcile
        // another company's columns against this one's fields.
        CompanyScopeService.clearCompanyScopedStorage();

        // Switching company changes the caller's scope, so a cached permission list could show
        // controls the user no longer has in the company they just moved to.
        this.myPermissions.clear();

        this.authService.applySwitchedTokens(tokens);
      }),
    );
  }

  /** Clears every company-shaped grid key, across all scopes. */
  static clearCompanyScopedStorage(): void {
    const prefixes = CompanyScopeService.companyScopedStorageKeys;

    Object.keys(localStorage)
      .filter((key) => prefixes.some((p) => key === p || key.startsWith(`${p}:`)))
      .forEach((key) => localStorage.removeItem(key));
  }
}
